import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '../components/Buttons/Button.js';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { toast } from '../utils/toast.js';
import { useRegister, useRegisterSocial, useLogin } from '@digiwedge/hooks-auth-web';
import { authApi } from '@digiwedge/idp-client';
import { LocalStorageTourStorage } from '@digiwedge/tour-core';
import { completePlayerRegistration } from '@digiwedge/teetime-api-client';
import { ContentWrap } from '../components/ContentWrap.js';
import { PageContent } from '../components/PageContent.js';
import { LogoComponent } from '../components/Logo/LogoComponent.js';
import { SocialProviderLogo } from '../components/SocialProviderLogo.js';
import { Heading } from '../components/index.js';
import { useRegisterWizardStore, } from '../hooks/useRegisterWizardStore.js';
import { getMessage } from '../i18n/messages.js';
import { DoneScreen, StepAccountForm } from './RegistrationWizard/index.js';
import { buildSignupUrl, clearPendingSocialState, clearSocialLoginPopupFlow, getProviderLabel, markSocialLoginPopupFlow, notifySocialLoginPopup, parseSocialProviderId, readSocialLoginPopupFlow, SOCIAL_LOGIN_PROVIDERS, TEE_TIME_WEB_OAUTH_AUDIENCE, } from '../utils/socialAuth.js';
import { buildExistingIdentityServerError, sanitizeAccountReturnTo, } from '../utils/accountRecovery.js';
import { MfaSetupFlow } from './MfaSetupFlow.js';
/** Type guard for AxiosError. */
function isAxiosError(error) {
    return typeof error === 'object' && error !== null && error.isAxiosError === true;
}
/** Helper to map our WizardStep to a Steps index (0 to 3). */
function getStepIndex(step) {
    switch (step) {
        case 'SOCIAL':
            return 0;
        case 'ACCOUNT':
            return 1;
        case 'MFA_PHONE':
            return 2;
        case 'DONE':
            return 3;
    }
}
function getPrevStep(step) {
    switch (step) {
        case 'ACCOUNT':
            return 'SOCIAL';
        case 'MFA_PHONE':
            return 'ACCOUNT';
        default:
            return null; // 'SOCIAL' and 'DONE' have no back navigation
    }
}
const ENABLE_ONBOARDING_TOUR = false; // Temporarily disable onboarding tour triggers
const shouldLogDebug = process.env['DEBUG_TEE_TIME_UI'] === '1';
function sanitizeSignupReturnTo(candidate) {
    return sanitizeAccountReturnTo(candidate);
}
function getSignupReturnTo() {
    if (typeof window === 'undefined') {
        return '/';
    }
    try {
        return sanitizeSignupReturnTo(new URLSearchParams(window.location.search).get('returnTo'));
    }
    catch {
        return '/';
    }
}
function startSocialSignup(provider, policyProfile) {
    const returnTo = `${window.location.origin}/signup`;
    const url = new URL(buildSignupUrl(provider, { profile: policyProfile }));
    url.searchParams.set('returnTo', returnTo);
    window.location.href = url.toString();
}
function closePopupWindowSoon() {
    if (typeof window === 'undefined')
        return;
    try {
        window.close();
    }
    catch {
        /* ignore close errors */
    }
}
export const RegisterPage = ({ oauthPolicyProfile = TEE_TIME_WEB_OAUTH_AUDIENCE, }) => {
    const step = useRegisterWizardStore((state) => state.step);
    const setStep = useRegisterWizardStore((state) => state.setStep);
    const userId = useRegisterWizardStore((state) => state.userId);
    const setUserId = useRegisterWizardStore((state) => state.setUserId);
    const accountDetails = useRegisterWizardStore((state) => state.accountDetails);
    const setAccountDetails = useRegisterWizardStore((state) => state.setAccountDetails);
    const [serverError, setServerError] = useState(null);
    const [processingProvider, setProcessingProvider] = useState(null);
    const [completingRegistration, setCompletingRegistration] = useState(false);
    const [signupMfaTenantId, setSignupMfaTenantId] = useState(undefined);
    const [signupMfaEmail, setSignupMfaEmail] = useState('');
    const processedSignupRef = useRef(false);
    // Hooks calling backend
    const { register: doRegister, loading: loadingRegister } = useRegister();
    const { registerSocial, loading: loadingRegisterSocial } = useRegisterSocial();
    const { login: doLogin } = useLogin();
    useEffect(() => {
        if (typeof window === 'undefined')
            return;
        const params = new URLSearchParams(window.location.search);
        if (params.get('oauthPopup') !== '1')
            return;
        const provider = parseSocialProviderId(params.get('provider'));
        const flowId = params.get('flowId')?.trim() ?? '';
        const flowToken = params.get('flowToken')?.trim() ?? '';
        if (provider && flowId && flowToken) {
            markSocialLoginPopupFlow(provider, flowId, flowToken, 'signup');
        }
    }, []);
    const refreshSignupMfaContext = useCallback(async () => {
        const profileResponse = await authApi.authControllerGetProfile();
        const profile = profileResponse.data;
        setSignupMfaTenantId(profile.tenantIds?.[0]);
        setSignupMfaEmail(profile.email ?? '');
        return profile;
    }, []);
    const completeRegistrationForUser = useCallback(async () => {
        setServerError(null);
        setCompletingRegistration(true);
        try {
            await completePlayerRegistration();
            await refreshSignupMfaContext();
            return true;
        }
        catch {
            const message = 'Unable to complete registration. Please try again.';
            setServerError({ message });
            toast.error(message);
            return false;
        }
        finally {
            setCompletingRegistration(false);
        }
    }, [refreshSignupMfaContext, setCompletingRegistration, setServerError]);
    //
    // STEP A: Account details
    //
    async function handleAccountSubmit(values) {
        setServerError(null);
        try {
            const response = await doRegister(values);
            setUserId(response.userId);
            const { password: omittedPassword, ...detailsToPersist } = values;
            void omittedPassword;
            setAccountDetails(detailsToPersist);
            try {
                const loginDto = {
                    email: values.email,
                    password: values.password,
                };
                const loginResult = await doLogin(loginDto);
                if (shouldLogDebug) {
                    console.log('[RegisterPage] doLogin result', loginResult);
                }
                toast.success(getMessage('en-US', 'loginSuccess'));
            }
            catch (loginErr) {
                toast.error(getMessage('en-US', 'loginCheckError'));
                throw loginErr;
            }
            const completed = await completeRegistrationForUser();
            if (!completed) {
                return;
            }
            toast.success(getMessage('en-US', 'accountCreatedSuccess'));
            setStep('MFA_PHONE');
        }
        catch (err) {
            if (isAxiosError(err)) {
                const status = err.response?.status;
                if (status === 409) {
                    const normalizedEmail = values.email.trim().toLowerCase();
                    const branchError = buildExistingIdentityServerError(normalizedEmail, getSignupReturnTo());
                    setServerError(branchError);
                    toast.error(branchError.message);
                    return;
                }
                setServerError({ message: getMessage('en-US', 'accountCreateFailed') });
            }
            else {
                setServerError({
                    message: getMessage('en-US', 'genericError'),
                });
            }
            toast.error(getMessage('en-US', 'accountCreateFailed'));
        }
    }
    /**
     * OAuth Social Signup Callback Handler
     *
     * Processes the OAuth redirect after successful social-provider authentication.
     * The IDP redirects here with: #signup_token=<token>&provider=<name>
     *
     * Flow (see apps/idp/README.md "UI Callback Contract"):
     * 1. Parse signup_token from URL fragment
     * 2. Call POST /auth/register-social to create user + link OAuth account
     * 3. Handle LoginResponseDto:
     *    - Tokens are stored automatically by useRegisterSocial hook
     *    - Route based on mfaRequired flag:
     *      • mfaRequired=true  → redirect to MFA_PHONE setup
     *      • mfaRequired=false → user is fully authenticated, go to DONE
     * 4. Clear URL fragment to prevent reprocessing
     * 5. Show success toast and trigger onboarding if applicable
     */
    useEffect(() => {
        if (typeof window === 'undefined')
            return;
        if (processedSignupRef.current)
            return;
        const { hash, pathname } = window.location;
        if (!hash)
            return;
        const params = new URLSearchParams(hash.startsWith('#') ? hash.substring(1) : hash);
        const signupToken = params.get('signup_token');
        if (!signupToken)
            return;
        processedSignupRef.current = true;
        window.history.replaceState(null, '', pathname);
        const providerParam = params.get('provider');
        const providerLabel = getProviderLabel(providerParam);
        setProcessingProvider(providerLabel);
        (async () => {
            try {
                const result = await registerSocial({
                    signup_token: signupToken,
                    termsAccepted: true,
                    remember: true, // Persist auth across sessions
                });
                let resolvedUserId = 'user' in result ? (result.user?.userId ?? '') : '';
                if (!resolvedUserId) {
                    try {
                        const profile = await refreshSignupMfaContext();
                        resolvedUserId = profile.userId;
                    }
                    catch (profileErr) {
                        throw profileErr instanceof Error
                            ? profileErr
                            : new Error(getMessage('en-US', 'genericError'));
                    }
                }
                if (!resolvedUserId) {
                    throw new Error(getMessage('en-US', 'genericError'));
                }
                setUserId(resolvedUserId);
                const linked = await completeRegistrationForUser();
                if (!linked) {
                    setProcessingProvider(null);
                    processedSignupRef.current = false;
                    return;
                }
                // Route based on MFA requirement (result is LoginResponseDto | MfaChallengeDto)
                if (result?.mfaRequired) {
                    toast.success(getMessage('en-US', 'accountCreatedSuccess'));
                    setStep('MFA_PHONE');
                    setProcessingProvider(null);
                }
                else {
                    // User is fully authenticated - tokens already stored by useRegisterSocial
                    toast.success(getMessage('en-US', 'loginSuccess'));
                    setStep('DONE');
                    setProcessingProvider(null);
                    const popupFlow = readSocialLoginPopupFlow();
                    if (popupFlow) {
                        clearPendingSocialState();
                        notifySocialLoginPopup({
                            flowId: popupFlow.flowId,
                            flowToken: popupFlow.flowToken,
                            status: 'complete',
                            ok: true,
                            provider: popupFlow.provider,
                            mode: popupFlow.mode,
                        });
                        clearSocialLoginPopupFlow();
                        closePopupWindowSoon();
                        return;
                    }
                    if (ENABLE_ONBOARDING_TOUR) {
                        const storage = new LocalStorageTourStorage();
                        storage.set('teetime.tour.v1:trigger', '1');
                        storage.remove('teetime.tour.v1:teetime-onboarding-v1:completed');
                    }
                }
            }
            catch {
                const message = 'Unable to complete social sign-up. Please try again.';
                toast.error(message);
                const popupFlow = readSocialLoginPopupFlow();
                if (popupFlow) {
                    notifySocialLoginPopup({
                        flowId: popupFlow.flowId,
                        flowToken: popupFlow.flowToken,
                        status: 'complete',
                        ok: false,
                        provider: popupFlow.provider,
                        mode: popupFlow.mode,
                        reason: 'signup-failed',
                        error: message,
                    });
                    clearSocialLoginPopupFlow();
                }
                setProcessingProvider(null);
                processedSignupRef.current = false;
            }
        })();
    }, [completeRegistrationForUser, refreshSignupMfaContext, registerSocial, setStep, setUserId]);
    const handleMfaVerified = useCallback(() => {
        setStep('DONE');
        const popupFlow = readSocialLoginPopupFlow();
        if (popupFlow) {
            clearPendingSocialState();
            notifySocialLoginPopup({
                flowId: popupFlow.flowId,
                flowToken: popupFlow.flowToken,
                status: 'complete',
                ok: true,
                provider: popupFlow.provider,
                mode: popupFlow.mode,
            });
            clearSocialLoginPopupFlow();
            closePopupWindowSoon();
            return;
        }
        if (ENABLE_ONBOARDING_TOUR) {
            const storage = new LocalStorageTourStorage();
            storage.set('teetime.tour.v1:trigger', '1');
            storage.remove('teetime.tour.v1:teetime-onboarding-v1:completed');
        }
    }, [setStep]);
    //
    // Render wizard step content
    //
    let content;
    if (step === 'SOCIAL') {
        content = (_jsxs("div", { className: "flex flex-col gap-3", children: [processingProvider && loadingRegisterSocial && (_jsxs("div", { className: "text-sm text-gray-600 text-center", role: "status", children: ["Completing ", processingProvider, " signup..."] })), SOCIAL_LOGIN_PROVIDERS.map((provider) => (_jsxs("button", { type: "button", onClick: () => startSocialSignup(provider.id, oauthPolicyProfile), disabled: loadingRegisterSocial, className: "flex w-full items-center justify-center gap-2 rounded-md border border-primary bg-white px-4 py-2 text-primary shadow-sm transition hover:bg-primary/5 disabled:cursor-not-allowed disabled:opacity-60", children: [_jsx(SocialProviderLogo, { provider: provider.id }), _jsx("span", { children: provider.label.replace('Login with', 'Continue with') })] }, provider.id))), _jsxs("div", { className: "flex items-center gap-3 py-1", "aria-hidden": "true", children: [_jsx("span", { className: "h-px flex-1 bg-gray-200" }), _jsx("span", { className: "text-xs font-medium uppercase tracking-wide text-gray-500", children: "or use email" }), _jsx("span", { className: "h-px flex-1 bg-gray-200" })] }), _jsx("button", { type: "button", onClick: () => setStep('ACCOUNT'), className: "w-full border border-primary text-primary py-2 rounded-md hover:bg-primary/10", children: getMessage('en-US', 'continueWithEmail') })] }));
    }
    else if (step === 'ACCOUNT') {
        content = (_jsx(StepAccountForm, { onNext: handleAccountSubmit, loading: loadingRegister || completingRegistration, serverError: serverError, initialValues: accountDetails, onServerErrorClear: () => setServerError(null) }));
    }
    else if (step === 'MFA_PHONE') {
        content = (_jsx(MfaSetupFlow, { userId: userId || undefined, email: signupMfaEmail || accountDetails.email || '', tenantId: signupMfaTenantId, onVerified: handleMfaVerified, requireTenantForWhatsapp: true }));
    }
    else {
        // DONE
        content = _jsx(DoneScreen, {});
    }
    const stepIndex = getStepIndex(step);
    const steps = [
        { value: 'SOCIAL', label: getMessage('en-US', 'registrationStepSocial') },
        { value: 'ACCOUNT', label: getMessage('en-US', 'registrationStepAccount') },
        { value: 'MFA_PHONE', label: getMessage('en-US', 'registrationStepPhone') },
        { value: 'DONE', label: getMessage('en-US', 'registrationStepDone') },
    ];
    const canNavigateToStep = (target) => {
        const targetIndex = getStepIndex(target);
        if (targetIndex <= stepIndex) {
            return true;
        }
        if (target === 'ACCOUNT' && step === 'SOCIAL') {
            return true;
        }
        return Boolean(userId) && target === 'MFA_PHONE' && stepIndex >= getStepIndex('ACCOUNT');
    };
    return (_jsx(PageContent, { children: _jsx(ContentWrap, { className: "flex min-h-screen items-center justify-center py-8 md:justify-end", children: _jsxs("div", { className: "relative flex w-full max-w-md flex-col gap-4 rounded-lg border border-gray-100 bg-white px-4 py-8 shadow-lg sm:px-8", children: [_jsx("div", { className: "text-center", children: _jsx(LogoComponent, { themeMode: "light", size: "large" }) }), _jsx(Heading, { variant: "h4", className: "text-center text-xl sm:text-2xl font-semibold", children: getMessage('en-US', 'registerAccountHeading') }), _jsx("div", { className: "flex items-center justify-between absolute top-4 left-4", children: _jsx("div", { children: getPrevStep(step) && (_jsx(Button, { "aria-label": getMessage('en-US', 'backButton'), variant: "secondary", size: "small", shape: "circle", icon: _jsx(ArrowLeftIcon, { className: "w-4 h-4" }), onClick: () => {
                                    const prev = getPrevStep(step);
                                    if (prev)
                                        setStep(prev);
                                } })) }) }), _jsx("ol", { className: "flex justify-between mb-6", children: steps.map(({ value, label }, idx) => {
                            const isComplete = stepIndex >= idx;
                            const isCurrent = step === value;
                            const canNavigate = canNavigateToStep(value);
                            return (_jsx("li", { className: "flex flex-1 justify-center text-xs", children: _jsxs("button", { type: "button", disabled: !canNavigate, onClick: () => {
                                        if (canNavigate)
                                            setStep(value);
                                    }, className: "group flex flex-col items-center focus:outline-none focus-visible:ring-2 focus-visible:ring-green-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-40", "aria-current": isCurrent ? 'step' : undefined, "aria-disabled": !canNavigate, children: [_jsx("span", { className: `w-6 h-6 rounded-full flex items-center justify-center mb-1 transition-colors ${isComplete ? 'bg-green-600 text-white group-hover:bg-green-700' : 'bg-gray-200 text-gray-500 group-hover:bg-gray-300'} ${isCurrent ? 'ring-2 ring-green-500 ring-offset-2 ring-offset-white' : ''}`, children: idx + 1 }), _jsx("span", { className: `${isComplete ? 'text-green-600 font-medium' : 'text-gray-500'} group-hover:text-green-600 text-center`, children: label })] }) }, value));
                        }) }), content] }) }) }));
};
//# sourceMappingURL=RegisterPage.js.map